import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined, RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import { RefundOrder, refundOrderService } from '../../services/paymentService';
import { useHorizontalTableWheel } from '../../hooks/useHorizontalTableWheel';

const { TextArea } = Input;

const statusMap: Record<string, { label: string; color: string }> = {
  pending_review: { label: '待审核', color: 'default' },
  processing: { label: '处理中', color: 'processing' },
  success: { label: '退款成功', color: 'success' },
  rejected: { label: '已驳回', color: 'error' },
  failed: { label: '处理失败', color: 'error' },
};

const channelMap: Record<string, { label: string; color: string }> = {
  wechat: { label: '微信支付', color: 'green' },
  alipay: { label: '支付宝', color: 'blue' },
  wallet: { label: '钱包', color: 'orange' },
};

const applyTypeMap: Record<string, string> = {
  refund: '申请退款',
  aftersale: '申请售后',
};

const auditModeMap: Record<string, string> = {
  auto_review: '自动审核',
  manual_review: '人工审核',
};

const RefundOrderPage: React.FC = () => {
  const [orders, setOrders] = useState<RefundOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [searchForm] = Form.useForm();
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<RefundOrder | null>(null);
  const [reviewVisible, setReviewVisible] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [reviewForm] = Form.useForm();
  const [reviewLoading, setReviewLoading] = useState(false);
  const tableWrapRef = useHorizontalTableWheel();

  const loadOrders = async (page = 1, pageSize = 10, params = {}) => {
    try {
      setLoading(true);
      const response = await refundOrderService.list({ page, pageSize, ...params });
      const list = response?.data?.list || response?.list || [];
      const total = response?.data?.total || response?.total || 0;
      setOrders(list);
      setPagination({ page, pageSize, total });
    } catch (error) {
      console.error('加载退款申请失败', error);
      message.error('加载退款申请失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOrders();
  }, []);

  const handleSearch = (values: any) => {
    void loadOrders(1, pagination.pageSize, values);
  };

  const openDetail = async (record: RefundOrder) => {
    try {
      const response = await refundOrderService.get(record.id);
      const data = response?.data || response;
      setCurrentOrder(data);
      setDetailVisible(true);
    } catch (error) {
      console.error('加载退款详情失败', error);
      message.error('加载退款详情失败');
    }
  };

  const openReview = (record: RefundOrder, action: 'approve' | 'reject') => {
    setCurrentOrder(record);
    setReviewAction(action);
    reviewForm.setFieldsValue({ remark: '', refundAmount: Number(record.refundAmount || 0) });
    setReviewVisible(true);
  };

  const handleReviewSubmit = async () => {
    if (!currentOrder) return;
    try {
      const values = await reviewForm.validateFields();
      setReviewLoading(true);
      await refundOrderService.review(currentOrder.id, {
        action: reviewAction,
        remark: values.remark,
        refundAmount: reviewAction === 'approve' ? Number(values.refundAmount || 0) : undefined,
      });
      message.success(reviewAction === 'approve' ? '审核通过成功' : '驳回成功');
      setReviewVisible(false);
      await loadOrders(pagination.page, pagination.pageSize, searchForm.getFieldsValue());
      if (detailVisible) {
        await openDetail(currentOrder);
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error('审核退款申请失败', error);
      }
    } finally {
      setReviewLoading(false);
    }
  };

  const columns = [
    { title: '退款单号', dataIndex: 'refundNo', key: 'refundNo', width: 180, fixed: 'left' as const },
    { title: '订单号', dataIndex: 'orderNo', key: 'orderNo', width: 180 },
    {
      title: '申请类型',
      dataIndex: 'applyType',
      key: 'applyType',
      width: 120,
      render: (value: string) => applyTypeMap[value] || value || '-',
    },
    {
      title: '审核方式',
      dataIndex: 'auditMode',
      key: 'auditMode',
      width: 120,
      render: (value: string) => auditModeMap[value] || value || '-',
    },
    {
      title: '支付渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (channel: string) => {
        const config = channelMap[channel] || { label: channel || '-', color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '退款金额',
      dataIndex: 'refundAmount',
      key: 'refundAmount',
      width: 120,
      render: (value: number) => `¥${Number(value || 0).toFixed(2)}`,
    },
    { title: '申请原因', dataIndex: 'reason', key: 'reason', width: 180, ellipsis: true },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const config = statusMap[status] || { label: status || '-', color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    { title: '申请人', dataIndex: 'userName', key: 'userName', width: 120 },
    { title: '处理人', dataIndex: 'operatorName', key: 'operatorName', width: 120, render: (value: string) => value || '-' },
    { title: '申请时间', dataIndex: 'createdAt', key: 'createdAt', width: 170 },
    { title: '审核时间', dataIndex: 'reviewedAt', key: 'reviewedAt', width: 170, render: (value: string) => value || '-' },
    { title: '退款时间', dataIndex: 'refundedAt', key: 'refundedAt', width: 170, render: (value: string) => value || '-' },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right' as const,
      render: (_: unknown, record: RefundOrder) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void openDetail(record)}>
            详情
          </Button>
          {record.status === 'pending_review' && (
            <>
              <Button type="link" size="small" icon={<CheckOutlined />} onClick={() => openReview(record, 'approve')}>
                通过
              </Button>
              <Button danger type="link" size="small" icon={<CloseOutlined />} onClick={() => openReview(record, 'reject')}>
                驳回
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card title={<><RollbackOutlined /> 退款申请</>}>
      <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }} onFinish={handleSearch}>
        <Form.Item name="refundNo">
          <Input placeholder="退款单号" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="orderNo">
          <Input placeholder="订单号" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="applyType">
          <Select
            placeholder="申请类型"
            allowClear
            style={{ width: 130 }}
            options={[
              { value: 'refund', label: '申请退款' },
              { value: 'aftersale', label: '申请售后' },
            ]}
          />
        </Form.Item>
        <Form.Item name="auditMode">
          <Select
            placeholder="审核方式"
            allowClear
            style={{ width: 130 }}
            options={[
              { value: 'manual_review', label: '人工审核' },
              { value: 'auto_review', label: '自动审核' },
            ]}
          />
        </Form.Item>
        <Form.Item name="status">
          <Select
            placeholder="处理状态"
            allowClear
            style={{ width: 130 }}
            options={[
              { value: 'pending_review', label: '待审核' },
              { value: 'processing', label: '处理中' },
              { value: 'success', label: '退款成功' },
              { value: 'rejected', label: '已驳回' },
              { value: 'failed', label: '处理失败' },
            ]}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              搜索
            </Button>
            <Button
              onClick={() => {
                searchForm.resetFields();
                void loadOrders();
              }}
            >
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <div ref={tableWrapRef}>
        <Table
          dataSource={orders}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1800 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: total => `共 ${total} 条`,
            onChange: (page, pageSize) => void loadOrders(page, pageSize, searchForm.getFieldsValue()),
          }}
        />
      </div>

      <Modal title="退款申请详情" open={detailVisible} onCancel={() => setDetailVisible(false)} footer={null} width={760}>
        {currentOrder && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="退款单号">{currentOrder.refundNo}</Descriptions.Item>
            <Descriptions.Item label="订单号">{currentOrder.orderNo}</Descriptions.Item>
            <Descriptions.Item label="申请类型">{applyTypeMap[currentOrder.applyType || ''] || '-'}</Descriptions.Item>
            <Descriptions.Item label="审核方式">{auditModeMap[currentOrder.auditMode || ''] || '-'}</Descriptions.Item>
            <Descriptions.Item label="订单状态">{currentOrder.orderStatus || '-'}</Descriptions.Item>
            <Descriptions.Item label="退款金额">¥{Number(currentOrder.refundAmount || 0).toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="支付渠道">
              {channelMap[currentOrder.channel]?.label || currentOrder.channel || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="处理状态">
              <Tag color={statusMap[currentOrder.status]?.color}>{statusMap[currentOrder.status]?.label || currentOrder.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="申请人">{currentOrder.userName || '-'}</Descriptions.Item>
            <Descriptions.Item label="处理人">{currentOrder.operatorName || '-'}</Descriptions.Item>
            <Descriptions.Item label="申请原因" span={2}>{currentOrder.reason || '-'}</Descriptions.Item>
            <Descriptions.Item label="详细说明" span={2}>{currentOrder.description || '-'}</Descriptions.Item>
            <Descriptions.Item label="申请图片" span={2}>
              {currentOrder.images?.length ? (
                <Space wrap>
                  {currentOrder.images.map((url, index) => (
                    <Image key={`${url}-${index}`} src={url} width={72} height={72} style={{ borderRadius: 8, objectFit: 'cover' }} />
                  ))}
                </Space>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="申请时间">{currentOrder.createdAt || '-'}</Descriptions.Item>
            <Descriptions.Item label="审核时间">{currentOrder.reviewedAt || '-'}</Descriptions.Item>
            <Descriptions.Item label="退款时间">{currentOrder.refundedAt || '-'}</Descriptions.Item>
            <Descriptions.Item label="退款流水号">{currentOrder.tradeNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="处理备注" span={2}>{currentOrder.remark || '-'}</Descriptions.Item>
            <Descriptions.Item label="失败原因" span={2}>
              {currentOrder.errorMsg ? <span style={{ color: '#ff4d4f' }}>{currentOrder.errorMsg}</span> : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={reviewAction === 'approve' ? '审核通过' : '驳回申请'}
        open={reviewVisible}
        onCancel={() => setReviewVisible(false)}
        onOk={() => void handleReviewSubmit()}
        confirmLoading={reviewLoading}
      >
        <Form form={reviewForm} layout="vertical">
          {reviewAction === 'approve' && (
            <Form.Item
              label="退款金额"
              name="refundAmount"
              rules={[
                { required: true, message: '请输入退款金额' },
                {
                  validator: async (_, value) => {
                    const amount = Number(value);
                    const maxAmount = Number(currentOrder?.refundAmount || 0);
                    if (!Number.isFinite(amount) || amount <= 0) {
                      throw new Error('退款金额必须大于0');
                    }
                    if (maxAmount > 0 && amount - maxAmount > 0.000001) {
                      throw new Error(`退款金额不能超过 ¥${maxAmount.toFixed(2)}`);
                    }
                  },
                },
              ]}
              extra={`当前申请金额：¥${Number(currentOrder?.refundAmount || 0).toFixed(2)}，最终会由后端按支付金额再次校验`}
            >
              <InputNumber
                min={0.01}
                precision={2}
                style={{ width: '100%' }}
                placeholder="请输入退款金额"
              />
            </Form.Item>
          )}
          <Form.Item
            label={reviewAction === 'approve' ? '审核备注' : '驳回原因'}
            name="remark"
            rules={reviewAction === 'reject' ? [{ required: true, message: '请输入驳回原因' }] : []}
          >
            <TextArea rows={4} maxLength={500} placeholder={reviewAction === 'approve' ? '请输入审核备注，可不填' : '请输入驳回原因'} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default RefundOrderPage;
